import { Component, Inject } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { finalize } from 'rxjs/operators';

import { FinancingHold } from 'src/app/api/models';
import { FinancingHoldsService } from 'src/app/api/services';
import { describeFinancingHold, refusalMessage } from 'src/app/shared/financing-holds/financing-hold-text';

export interface ReleaseFinancingHoldsDialogData {
  /** The holds that apply to the invoice: its `financing_holds`. */
  holds: FinancingHold[];
}

interface ListedHold {
  hold: FinancingHold;
  // The name of the hold's checkbox control: its id.
  control: string;
  description: string;
}

/**
 * ReleaseFinancingHoldsDialogComponent releases several of the financing
 * holds that apply to an invoice with one required note
 * (place-and-release-several-financing-holds, design D6).
 *
 * It lists every hold of the invoice's `financing_holds`, each with a
 * checkbox, and ticks none. That is deliberate: a client's or an end
 * customer's hold also covers their other invoices, and a listed hold may
 * have been placed by someone else for another reason, so each release is a
 * tick the handler made. The dialog says so above the list.
 *
 * One `releaseFinancingHolds` request names every ticked hold, so the server
 * releases all of them or none. A refusal is shown inside the dialog, which
 * stays open with the note and the ticks. The server's message names the
 * holds a refusal is about ("Hold 3 is already released."), and the dialog
 * does not re-fetch: cancelling and reopening shows the current holds. It
 * closes with the released holds, and with `undefined` on cancel, escape or
 * a backdrop click.
 *
 * The disabled submit is a convenience only: the server trims the note and
 * refuses a blank one with `422 financing_hold_release_note_required`, and
 * an empty selection with `422 financing_hold_invalid_selection`.
 */
@Component({
  selector: 'app-release-financing-holds-dialog',
  templateUrl: './release-financing-holds-dialog.component.html',
  styleUrls: ['./release-financing-holds-dialog.component.scss'],
})
export class ReleaseFinancingHoldsDialogComponent {
  public readonly holds: ListedHold[];
  // One boolean control per listed hold, named by the hold's id.
  public readonly form: UntypedFormGroup;
  public saving = false;
  public errorMessage: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<ReleaseFinancingHoldsDialogComponent, FinancingHold[]>,
    @Inject(MAT_DIALOG_DATA) public data: ReleaseFinancingHoldsDialogData,
    private financingHoldsService: FinancingHoldsService,
  ) {
    this.holds = data.holds.map((hold) => ({
      hold,
      control: String(hold.id),
      description: describeFinancingHold(hold),
    }));
    const ticks: Record<string, UntypedFormControl> = {};
    for (const listed of this.holds) {
      ticks[listed.control] = new UntypedFormControl(false);
    }
    this.form = new UntypedFormGroup({
      holds: new UntypedFormGroup(ticks),
      note: new UntypedFormControl(''),
    });
  }

  // The trimmed note both gates the submit and is what is sent.
  public get note(): string {
    return ((this.form.value.note as string | null) ?? '').trim();
  }

  /** The ids of the ticked holds, in the listed order. */
  public get selectedHoldIds(): number[] {
    const ticks = (this.form.value.holds ?? {}) as Record<string, boolean>;
    return this.holds.filter((listed) => ticks[listed.control] === true).map((listed) => listed.hold.id);
  }

  // The submit says how many holds it releases: "Release hold", "Release 3 holds".
  public get submitLabel(): string {
    const count = this.selectedHoldIds.length;
    return count > 1 ? `Release ${count} holds` : 'Release hold';
  }

  public get canSubmit(): boolean {
    return !this.saving && this.selectedHoldIds.length > 0 && this.note !== '';
  }

  public submit(): void {
    if (!this.canSubmit) {
      return;
    }
    this.saving = true;
    this.errorMessage = null;
    this.financingHoldsService
      .releaseFinancingHolds({ body: { hold_ids: this.selectedHoldIds, note: this.note } })
      // A 403 completes without a value: the HTTP error interceptor shows
      // its own dialog. Re-enable the submit either way.
      .pipe(finalize(() => (this.saving = false)))
      .subscribe({
        next: (holds: FinancingHold[]) => this.dialogRef.close(holds),
        error: (err: unknown) => {
          this.errorMessage = refusalMessage(err, 'Could not release the holds. Please try again.');
        },
      });
  }

  public cancel(): void {
    this.dialogRef.close(undefined);
  }

  public trackByHoldId(_index: number, listed: ListedHold): number {
    return listed.hold.id;
  }
}
